import { signOut as supertokensSignOut } from 'supertokens-web-js/recipe/emailpassword'

interface FailureState {
  failures: number
  lastFailureAtMs: number
}

const FAILURE_STATE_STORAGE_KEY = 'authFailureState'
const FAILURE_WINDOW_MS = 2 * 60 * 1000
const FAILURE_THRESHOLD = 2
const PROMPT_THROTTLE_MS = 30 * 1000
const PROMPT_STATE_STORAGE_KEY = 'authFailurePromptAt'

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

const readFailureState = (): FailureState => {
  if (!isBrowser()) return { failures: 0, lastFailureAtMs: 0 }
  try {
    const raw = window.localStorage.getItem(FAILURE_STATE_STORAGE_KEY)
    if (!raw) return { failures: 0, lastFailureAtMs: 0 }
    const parsed = JSON.parse(raw) as FailureState
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.failures === 'number' &&
      typeof parsed.lastFailureAtMs === 'number'
    ) {
      return parsed
    }
    return { failures: 0, lastFailureAtMs: 0 }
  } catch {
    return { failures: 0, lastFailureAtMs: 0 }
  }
}

const writeFailureState = (state: FailureState): void => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(FAILURE_STATE_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore write errors
  }
}

export const resetAuthFailureCounter = (): void => {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(FAILURE_STATE_STORAGE_KEY)
    window.localStorage.removeItem(PROMPT_STATE_STORAGE_KEY)
  } catch {
    // ignore
  }
}

const getRootDomainVariants = (hostname: string): string[] => {
  const parts = hostname.split('.')
  const variants: string[] = []
  if (parts.length >= 2) {
    const root = parts.slice(-2).join('.')
    variants.push(root, `.${root}`)
  }
  variants.push(hostname, `.${hostname}`)
  return Array.from(new Set(variants))
}

const deleteCookieEverywhere = (name: string): void => {
  if (!isBrowser()) return
  const paths = ['/', '/auth']
  const domains = getRootDomainVariants(window.location.hostname)

  // Try without domain first
  for (const path of paths) {
    document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
  }

  for (const domain of domains) {
    for (const path of paths) {
      document.cookie = `${name}=; path=${path}; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
    }
  }
}

const clearSupertokensCookies = (): void => {
  if (!isBrowser()) return
  const knownTokenNames = ['sAccessToken', 'sRefreshToken', 'frontToken', 'rid', 'anti-csrf']

  // Delete known cookies explicitly
  for (const name of knownTokenNames) {
    deleteCookieEverywhere(name)
  }

  // Best-effort delete of all cookies in current scope
  const allCookies = document.cookie.split(';')
  for (const pair of allCookies) {
    const cookieName = pair.split('=')[0]?.trim()
    if (cookieName) {
      deleteCookieEverywhere(cookieName)
    }
  }
}

const clearWebStorage = (): void => {
  if (!isBrowser()) return
  try {
    // Be careful to avoid throwing in private mode
    window.sessionStorage.clear()
  } catch {
    // ignore
  }
  try {
    window.localStorage.clear()
  } catch {
    // ignore
  }
}

export const hardResetAuth = async (redirectBackUrl?: string): Promise<void> => {
  if (!isBrowser()) return
  try {
    // Attempt server-side signout to clear httpOnly cookies
    const signOutPromise = supertokensSignOut()
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000))
    await Promise.race([signOutPromise.then(() => {}), timeout])
  } catch {
    // ignore errors and continue with client-side cleanup
  }

  try {
    clearSupertokensCookies()
    clearWebStorage()
    resetAuthFailureCounter()
  } finally {
    const redirectTarget = redirectBackUrl ?? '/'
    window.location.replace(redirectTarget)
  }
}

export const recordAuthFailureAndMaybeRecover = async (options?: {
  isExpected?: boolean
  endpointName?: string
}): Promise<void> => {
  if (!isBrowser()) return

  // Don't record expected 401s (e.g., when there's no session and we're trying to create one,
  // or during signout flow)
  if (options?.isExpected) {
    return
  }

  const now = Date.now()
  const current = readFailureState()
  const inWindow = now - current.lastFailureAtMs <= FAILURE_WINDOW_MS
  const nextFailures = inWindow ? current.failures + 1 : 1
  writeFailureState({ failures: nextFailures, lastFailureAtMs: now })

  // At or above threshold, prompt the user (throttled) without re-triggering refresh loops
  if (nextFailures >= FAILURE_THRESHOLD) {
    let lastPromptAt = 0
    try {
      const raw = window.localStorage.getItem(PROMPT_STATE_STORAGE_KEY)
      lastPromptAt = raw ? parseInt(raw, 10) : 0
    } catch {}

    const shouldPrompt = Date.now() - lastPromptAt > PROMPT_THROTTLE_MS
    if (shouldPrompt) {
      const shouldClear = window.confirm(
        'We are seeing repeated sign-in issues. Clear session cookies and local data to recover?\n\nThis will sign you out and reload the page.'
      )
      try {
        window.localStorage.setItem(PROMPT_STATE_STORAGE_KEY, String(Date.now()))
      } catch {}
      if (shouldClear) {
        await hardResetAuth()
      }
    }
  }
}
